import { ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/authStore'

export function useTzDate() {
  const authStore = useAuthStore()
  const tz = authStore.tz || '+00:00'

  const parseOffsetMs = (tzStr: string): number => {
    const sign = tzStr[0] === '-' ? -1 : 1
    const hours = parseInt(tzStr.slice(1, 3), 10)
    const minutes = parseInt(tzStr.slice(4, 6), 10)
    return sign * (hours * 60 + minutes) * 60 * 1000
  }

  const offsetMs = parseOffsetMs(tz)

  const toTzDate = (date: Date | string | number): Date => {
    const d =
      typeof date === 'string'
        ? new Date(date)
        : typeof date === 'number'
          ? new Date(date)
          : date
    if (isNaN(d.getTime())) return d
    return new Date(d.getTime() + offsetMs)
  }

  const yyyymmdd = (date: Date | string | number): string => {
    const d = toTzDate(date)
    const mm = d.getUTCMonth() + 1
    const dd = d.getUTCDate()
    return [(dd > 9 ? '' : '0') + dd, (mm > 9 ? '' : '0') + mm, d.getUTCFullYear()].join('.')
  }

  const hhmm = (date: Date | string | number): string => {
    const d = toTzDate(date)
    const hh = d.getUTCHours()
    const mm = d.getUTCMinutes()
    return [(hh > 9 ? '' : '0') + hh, (mm > 9 ? '' : '0') + mm].join(':')
  }

  const hhmmss = (date: Date | string | number): string => {
    const d = toTzDate(date)
    const hh = d.getUTCHours()
    const mm = d.getUTCMinutes()
    const ss = d.getUTCSeconds()
    return [(hh > 9 ? '' : '0') + hh, (mm > 9 ? '' : '0') + mm, (ss > 9 ? '' : '0') + ss].join(':')
  }

  const dateFullStr = (date: Date | string | number): string => {
    return yyyymmdd(date) + ' ' + hhmmss(date)
  }

  const getDayOfWeek = (date: Date | string | number): number => {
    const d = toTzDate(date)
    return d.getUTCDay()
  }

  // now ref that updates every second — timestamp itself is timezone-agnostic,
  // but we keep it here so components can share the same reactive clock.
  const now = ref(new Date())
  let interval: ReturnType<typeof setInterval> | null = null

  onMounted(() => {
    interval = setInterval(() => {
      now.value = new Date()
    }, 1000)
  })

  onUnmounted(() => {
    if (interval) clearInterval(interval)
  })

  return { yyyymmdd, hhmm, hhmmss, dateFullStr, getDayOfWeek, toTzDate, now, offsetMs }
}
